import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Filter, 
  Download, 
  MoreHorizontal,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Check,
  X,
  MessageSquare,
  UserPlus
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import { apiRequest } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { ApproveRequestDialog } from '@/components/admin/ApproveRequestDialog';

interface AccessRequest {
  id: string;
  type: 'access_denied' | 'email_contact' | 'request_contact';
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  company_name: string;
  message: string;
  status: 'pending' | 'in_progress' | 'approved' | 'rejected' | 'completed';
  assigned_to: string | null;
  assigned_to_email: string | null;
  assigned_to_first_name: string | null;
  assigned_to_last_name: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

const AdminAccessRequests: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = useState<AccessRequest | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);

  // Fetch access requests
  const { data: requestsData, isLoading, error, refetch } = useQuery({
    queryKey: ['/api/admin/access-requests', statusFilter, typeFilter, currentPage],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('type', typeFilter);
      
      const data = await apiRequest(`/api/admin/access-requests?${params.toString()}`);
      return data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
    refetchOnWindowFocus: true,
  });

  const requests: AccessRequest[] = requestsData?.requests || [];
  const pagination = requestsData?.pagination || { total: 0, totalPages: 0 };

  // Update request mutation
  const updateRequestMutation = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status?: string; notes?: string }) => {
      return await apiRequest(`/api/admin/access-requests/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status, notes }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/access-requests'] });
      toast({
        title: "Success",
        description: "Request updated successfully",
      });
      setIsNoteDialogOpen(false);
      setNoteText('');
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update request",
        variant: "destructive",
      });
    },
  });

  // Approve request mutation
  const approveRequestMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return await apiRequest(`/api/admin/access-requests/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/access-requests'] });
      toast({
        title: "Success",
        description: "Access request approved successfully",
      });
      setIsApproveDialogOpen(false);
      setSelectedRequest(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to approve request",
        variant: "destructive",
      });
    },
  });

  const filteredRequests = requests.filter(request => {
    const matchesSearch = 
      request.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (request.first_name && request.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (request.last_name && request.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (request.company_name && request.company_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'access_denied':
        return <Badge className="bg-red-100 text-red-800"><AlertCircle className="w-3 h-3 mr-1" />Access Denied</Badge>;
      case 'email_contact':
        return <Badge className="bg-blue-100 text-blue-800"><Mail className="w-3 h-3 mr-1" />Email Contact</Badge>;
      case 'request_contact':
        return <Badge className="bg-green-100 text-green-800"><UserPlus className="w-3 h-3 mr-1" />Request Contact</Badge>;
      default:
        return <Badge variant="secondary">{type}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-800"><Clock className="w-3 h-3 mr-1" />In Progress</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      case 'completed':
        return <Badge className="bg-gray-100 text-gray-800"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewDetails = (request: AccessRequest) => {
    setSelectedRequest(request);
    setIsDetailDialogOpen(true);
  };

  const handleApprove = (request: AccessRequest) => {
    setSelectedRequest(request);
    setIsApproveDialogOpen(true);
  };

  const handleAddNote = (request: AccessRequest) => {
    setSelectedRequest(request);
    setNoteText(request.notes || '');
    setIsNoteDialogOpen(true);
  };

  const handleUpdateStatus = (request: AccessRequest, newStatus: string) => {
    updateRequestMutation.mutate({
      id: request.id,
      status: newStatus,
    });
  };

  const handleSaveNote = () => {
    if (!selectedRequest) return;
    updateRequestMutation.mutate({
      id: selectedRequest.id,
      notes: noteText,
    });
  };

  const handleApproveSubmit = (approveData: any) => {
    if (!selectedRequest) return;
    approveRequestMutation.mutate({
      id: selectedRequest.id,
      data: approveData,
    });
  };

  const exportToCSV = () => {
    const headers = ['Type', 'Email', 'Name', 'Company', 'Phone', 'Status', 'Created At', 'Resolved At'];
    const csvContent = [
      headers.join(','),
      ...filteredRequests.map(request => [
        request.type,
        request.email,
        `${request.first_name || ''} ${request.last_name || ''}`.trim(),
        request.company_name || '',
        request.phone || '',
        request.status,
        formatDate(request.created_at),
        request.resolved_at ? formatDate(request.resolved_at) : ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'access-requests-export.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading access requests...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-600 text-lg font-medium">Error loading access requests</div>
            <p className="mt-2 text-gray-600">Please try again later</p>
            <Button onClick={() => refetch()} className="mt-4">Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <AlertCircle className="w-8 h-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">
                  {requests.filter(r => r.status === 'pending').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Approved</p>
                <p className="text-2xl font-bold text-gray-900">
                  {requests.filter(r => r.status === 'approved').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Mail className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Requests</p>
                <p className="text-2xl font-bold text-gray-900">{pagination.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Clock className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-gray-900">
                  {requests.filter(r => r.status === 'in_progress').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search requests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="access_denied">Access Denied</SelectItem>
                <SelectItem value="email_contact">Email Contact</SelectItem>
                <SelectItem value="request_contact">Request Contact</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={exportToCSV}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold text-gray-900">Access Requests</CardTitle>
              <p className="text-sm text-gray-600 mt-1">Manage all access requests and free trial requests</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Type</TableHead>
                  <TableHead className="w-64">Email</TableHead>
                  <TableHead className="w-48">Name</TableHead>
                  <TableHead className="w-48">Company</TableHead>
                  <TableHead className="w-32">Status</TableHead>
                  <TableHead className="w-40">Created At</TableHead>
                  <TableHead className="w-40">Resolved At</TableHead>
                  <TableHead className="w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      No access requests found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>{getTypeBadge(request.type)}</TableCell>
                      <TableCell>
                        <a href={`mailto:${request.email}`} className="text-blue-600 hover:underline">
                          {request.email}
                        </a>
                      </TableCell>
                      <TableCell>
                        {request.first_name || request.last_name
                          ? `${request.first_name || ''} ${request.last_name || ''}`.trim()
                          : 'N/A'}
                      </TableCell>
                      <TableCell>{request.company_name || 'N/A'}</TableCell>
                      <TableCell>{getStatusBadge(request.status)}</TableCell>
                      <TableCell className="text-sm text-gray-600">{formatDate(request.created_at)}</TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {request.resolved_at ? formatDate(request.resolved_at) : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => handleViewDetails(request)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {request.status === 'pending' && (
                              <>
                                <DropdownMenuItem onClick={() => handleApprove(request)}>
                                  <Check className="w-4 h-4 mr-2" />
                                  Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleUpdateStatus(request, 'rejected')}
                                  className="text-red-600"
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Reject
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem onClick={() => handleAddNote(request)}>
                              <MessageSquare className="w-4 h-4 mr-2" />
                              Add Note
                            </DropdownMenuItem>
                            {request.status === 'pending' && (
                              <DropdownMenuItem 
                                onClick={() => handleUpdateStatus(request, 'in_progress')}
                              >
                                <Clock className="w-4 h-4 mr-2" />
                                Mark In Progress
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="p-4 border-t">
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.totalPages}
                totalItems={pagination.total}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Access Request Details</DialogTitle>
            <DialogDescription>
              View detailed information about this access request
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <div className="mt-1">{getTypeBadge(selectedRequest.type)}</div>
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <p className="mt-1 text-sm">{selectedRequest.email}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <p className="mt-1 text-sm">{selectedRequest.first_name || 'N/A'}</p>
                </div>
                <div>
                  <Label>Last Name</Label>
                  <p className="mt-1 text-sm">{selectedRequest.last_name || 'N/A'}</p>
                </div>
              </div>
              <div>
                <Label>Phone</Label>
                <p className="mt-1 text-sm">{selectedRequest.phone || 'N/A'}</p>
              </div>
              <div>
                <Label>Company Name</Label>
                <p className="mt-1 text-sm">{selectedRequest.company_name || 'N/A'}</p>
              </div>
              <div>
                <Label>Message</Label>
                <p className="mt-1 text-sm whitespace-pre-wrap">{selectedRequest.message || 'N/A'}</p>
              </div>
              <div>
                <Label>Notes</Label>
                <p className="mt-1 text-sm whitespace-pre-wrap">{selectedRequest.notes || 'No notes'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Created At</Label>
                  <p className="mt-1 text-sm">{formatDate(selectedRequest.created_at)}</p>
                </div>
                {selectedRequest.resolved_at && (
                  <div>
                    <Label>Resolved At</Label>
                    <p className="mt-1 text-sm">{formatDate(selectedRequest.resolved_at)}</p>
                  </div>
                )}
              </div>
              {selectedRequest.assigned_to_email && (
                <div>
                  <Label>Assigned To</Label>
                  <p className="mt-1 text-sm">
                    {selectedRequest.assigned_to_first_name} {selectedRequest.assigned_to_last_name} ({selectedRequest.assigned_to_email})
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>
              Add or update notes for this access request
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="note">Notes</Label>
              <Textarea
                id="note"
                rows={6}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Enter notes about this request..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNoteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveNote} disabled={updateRequestMutation.isPending}>
              {updateRequestMutation.isPending ? 'Saving...' : 'Save Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      {selectedRequest && (
        <ApproveRequestDialog
          isOpen={isApproveDialogOpen}
          onClose={() => {
            setIsApproveDialogOpen(false);
            setSelectedRequest(null);
          }}
          request={selectedRequest}
          onApprove={handleApproveSubmit}
          isSubmitting={approveRequestMutation.isPending}
        />
      )}
    </div>
  );
};

export default AdminAccessRequests;

